import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "../components/Seo";

const FAQ = [
  {
    q: "What is the Martingale betting system?",
    a: "The Martingale betting system is a progressive staking strategy where you double your wager after every losing bet. The idea is that a single winning bet recovers all prior losses and nets you a profit equal to your original base stake. It originated in 18th-century France, where gamblers used it on even-money coin-toss and roulette wagers — it takes its name from the French village of Martigues. It is not to be confused with the unrelated 20th-century probability-theory concept of a 'martingale' stochastic process, formalized by mathematician Paul Lévy in 1934. It remains one of the most studied betting progressions in existence.",
  },
  {
    q: "How does the Martingale method work in sports betting?",
    a: "Pick a near-coin-flip wager — a moneyline or spread priced close to -110 (52.4% implied probability). Place your base stake. If it loses, your next wager on a comparable bet doubles. Keep doubling after each loss until one bet wins: the winning payout recovers all prior losses plus one base unit of profit, and you reset to the base stake.",
  },
  {
    q: "What is the best Martingale betting example with $2,000 bankroll?",
    a: "With a $2,000 bankroll and a $200 base stake at -110 odds, the Classic Martingale sequence is $200 → $400 → $800 → $1,600. Four steps require $3,000 in total risk and return +$54.55 in profit per completed winning sequence. The probability of a 4-loss streak on a coin flip is 6.25%, so ~1 in 16 sequences will bust.",
  },
  {
    q: "What are the downsides of the Martingale method?",
    a: "Three things can destroy a Martingale run: (1) a long losing streak that exhausts your bankroll; (2) a sportsbook's table limit capping your doubling; (3) chasing bets with negative expected value so the streak arrives faster than Gaussian math predicts. Martingale shifts variance toward rare catastrophic losses — it does not create edge.",
  },
  {
    q: "What Martingale variants exist?",
    a: "Classic Martingale doubles stakes after each loss. Grand Martingale doubles plus one base unit after each loss (larger profits, higher bankroll). Mini Martingale caps the doubling at a set number of steps for survivability. Anti-Martingale (Reverse Martingale) doubles after wins to ride hot streaks. D'Alembert adds one unit per loss and subtracts one per win — slower and safer than doubling.",
  },
  {
    q: "What are the best sports for the Martingale method?",
    a: "Moneyline-style markets priced near -110 (MLB, NBA spread, NHL puck line, NFL spread, UFC pick'em fights) are ideal because they closely approximate 50/50 outcomes. Avoid heavy favorites (-250 or shorter) because the profit per unit is too small to justify the bankroll risk, and avoid props with wide vig.",
  },
  {
    q: "How much bankroll do I need for the Martingale method?",
    a: "At a $10 base stake and 7 max steps, Classic Martingale requires $1,270 in reserve. At a $100 base stake and 7 steps, you need $12,700. A safe rule is to size your base stake at 1-2% of bankroll and pre-commit a maximum step depth you will never exceed.",
  },
];

export default function MartingaleMethod() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-background grain relative text-foreground">
      <Seo
        title="The Martingale Method · Rules, Bankroll Math, and Sports Betting Examples"
        path="/martingale-method"
        description="Complete guide to the Martingale betting system: how the method works, step-by-step $2,000/$200 example, bankroll requirements, bust-risk math, all five variants, and how to apply it to MLB, NBA, NHL, NFL, and UFC sports betting."
        keywords="martingale, martingale method, martingale betting system, martingale strategy, double down betting, betting progression, bankroll math, betting system, sports betting martingale"
        jsonLd={jsonLd}
      />

      <header className="relative z-10 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-8 h-8 border border-[#00D4FF] rotate-45 flex items-center justify-center">
              <span className="-rotate-45 text-[#00D4FF] font-mono text-xs font-bold">M</span>
            </div>
            <div>
              <div className="font-display text-lg leading-none">Martingale</div>
              <div className="overline leading-none mt-1">PLAYBOOK·TERMINAL</div>
            </div>
          </Link>
          <div className="flex gap-2">
            <Link to="/arbitrage-betting" className="px-4 py-2 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-[#00D4FF]">Arbitrage</Link>
            <Link to="/pricing" className="px-4 py-2 text-xs font-mono uppercase tracking-widest border border-zinc-800 hover:border-[#00D4FF] hover:text-[#00D4FF]">Pricing</Link>
            <Link to="/register" className="px-4 py-2 text-xs font-mono uppercase tracking-widest bg-[#00D4FF] text-black">Start Free Trial</Link>
          </div>
        </div>
      </header>

      <article className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 py-16">
        <div className="overline mb-6">GUIDE · THE METHOD</div>
        <h1 className="font-display text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6">
          The Martingale Method, Explained
        </h1>
        <p className="text-lg text-zinc-400 leading-relaxed mb-12">
          The Martingale method is a 300-year-old progression strategy that has become
          one of the most recognized — and most debated — tools in sports betting,
          blackjack, and roulette. This guide covers exactly how it works, the math
          that makes it dangerous, how to size your bankroll, all five variants, and
          how we apply it daily across MLB, NBA, NHL, NFL, and UFC.
        </p>

        <h2 className="font-display text-3xl mt-12 mb-4 text-white">
          What is the Martingale Method?
        </h2>
        <p className="text-zinc-300 leading-relaxed mb-4">
          <strong>The Martingale method is a negative-progression betting system in
          which the bettor doubles the size of every wager after a loss.</strong> The
          goal is that a single winning bet recovers every prior loss in the sequence
          plus one unit of profit equal to the original base stake. It was first
          formalized in 18th-century France and is the canonical example of a
          variance-shifting strategy: it does not create positive expected value, but
          it trades small frequent wins for rare catastrophic losses.
        </p>

        <h2 className="font-display text-3xl mt-12 mb-4 text-white">
          How the Martingale Works in Sports Betting
        </h2>
        <ol className="space-y-3 text-zinc-300 list-decimal list-inside mb-6">
          <li>Select a near-50/50 wager — typically a moneyline or spread priced near -110.</li>
          <li>Place your base stake (commonly 1–2% of bankroll).</li>
          <li>If it wins, bank the profit and reset to the base stake.</li>
          <li>If it loses, double the stake and place another comparable wager.</li>
          <li>Continue doubling until a bet wins, then reset.</li>
        </ol>

        <h2 className="font-display text-3xl mt-12 mb-4 text-white">
          Worked Example · $2,000 Bankroll, $200 Base
        </h2>
        <div className="border border-zinc-800 my-6 overflow-hidden">
          <table className="w-full mono text-sm">
            <thead className="bg-zinc-900">
              <tr className="text-left">
                <th className="px-4 py-3 overline">STEP</th>
                <th className="px-4 py-3 overline">STAKE</th>
                <th className="px-4 py-3 overline">CUMULATIVE RISK</th>
                <th className="px-4 py-3 overline">RESULT → NEXT</th>
              </tr>
            </thead>
            <tbody>
              {[
                [1, 200, 200, "LOSS → $400"],
                [2, 400, 600, "LOSS → $800"],
                [3, 800, 1400, "LOSS → $1,600"],
                [4, 1600, 3000, "WIN → Reset +$54.55"],
              ].map(([step, stake, cum, next]) => (
                <tr key={step} className="border-t border-zinc-800">
                  <td className="px-4 py-3 text-zinc-400">#{step}</td>
                  <td className="px-4 py-3 text-white">${stake.toLocaleString()}</td>
                  <td className="px-4 py-3 text-zinc-400">${cum.toLocaleString()}</td>
                  <td className={`px-4 py-3 ${String(next).startsWith("WIN") ? "neon-text" : "text-red-400"}`}>{next}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-zinc-300 leading-relaxed">
          Four steps risk $3,000 total and return +$54.55 per winning sequence at -110.
          The probability of four consecutive losses on a coin-flip wager is 6.25%, or
          roughly one in sixteen sequences.
        </p>

        <h2 className="font-display text-3xl mt-12 mb-4 text-white">
          The Five Martingale Variants
        </h2>
        <div className="space-y-4 mb-6">
          {[
            ["Classic Martingale", "Double after every loss."],
            ["Grand Martingale", "Double + 1 base unit after every loss — bigger profits, bigger bankroll."],
            ["Mini Martingale", "Cap the doubling at N steps (commonly 4) to limit drawdown."],
            ["Anti-Martingale (Reverse)", "Double after every WIN, reset on loss — rides hot streaks."],
            ["D'Alembert", "+1 unit per loss, −1 per win — slower and safer."],
          ].map(([name, desc]) => (
            <div key={name} className="border border-zinc-800 p-5">
              <div className="font-display text-xl neon-text mb-1">{name}</div>
              <div className="text-sm text-zinc-400">{desc}</div>
            </div>
          ))}
        </div>

        <h2 className="font-display text-3xl mt-12 mb-4 text-white">
          Risks of the Martingale Method
        </h2>
        <ul className="space-y-2 text-zinc-300 mb-6">
          <li className="flex gap-2"><Check size={16} className="text-amber-500 mt-1 shrink-0" /> A 7-loss streak on a 50/50 wager occurs ~0.78% of the time — rare but real.</li>
          <li className="flex gap-2"><Check size={16} className="text-amber-500 mt-1 shrink-0" /> At -110, you need to win 52.38% of your wagers just to break even.</li>
          <li className="flex gap-2"><Check size={16} className="text-amber-500 mt-1 shrink-0" /> Every US sportsbook has a per-bet table limit between $2,500 and $10,000.</li>
          <li className="flex gap-2"><Check size={16} className="text-amber-500 mt-1 shrink-0" /> Negative-EV Martingale is still negative-EV — the math does not rescue a bad pick.</li>
        </ul>

        <h2 className="font-display text-3xl mt-12 mb-4 text-white">FAQ</h2>
        <div className="space-y-6 mb-10">
          {FAQ.map((f) => (
            <div key={f.q} className="border border-zinc-800 p-5">
              <h3 className="font-display text-xl text-white mb-2">{f.q}</h3>
              <p className="text-sm text-zinc-300 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 border border-[#00D4FF]/40 bg-[#00D4FF]/5 p-8 text-center">
          <div className="overline neon-text mb-3">READY TO START</div>
          <h2 className="font-display text-3xl mb-4">
            Stop calculating. Start rolling.
          </h2>
          <p className="text-zinc-300 mb-6 max-w-xl mx-auto">
            Get AI-researched Martingale picks for MLB, NBA, NHL, NFL, and UFC delivered
            every morning. 3 days free, then $9.99/week.
          </p>
          <Link
            to="/register"
            data-testid="martingale-cta-register"
            className="inline-flex items-center gap-2 px-6 py-4 bg-[#00D4FF] text-black font-mono uppercase tracking-widest text-xs"
          >
            Start 3 Free Days <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </article>

      <footer className="relative z-10 border-t border-zinc-800 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-xs mono text-zinc-500 text-center">
          MARTINGALE PLAYBOOK · GAMBLE RESPONSIBLY · 1-800-GAMBLER
        </div>
      </footer>
    </div>
  );
}
